import { Outlet } from "react-router";
import { useEffect, useState } from "react";
import { useLocation } from 'react-router';
import { useAnimatedNavigate } from "../../hooks/useAnimatedNavigate";
import { useDispatch, useSelector } from "react-redux";
import { setStatus } from "../../features/statusReducer";
import { PageLoader } from "../../components/loader/pageLoader";
import UserAPI from "../../api/UserAPI";
import { clearAuthSession, getAccessToken } from "../../utils/authStorage";
import { fetchAdminInfo, fetchUserInfo, resetUserState } from "../../features/userReducer";

import "./zeroStyle.css";
import "./pageStyle.css";
import "./colorScheme.css";

export const App = () => {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith("/auth") || location.pathname.startsWith("/invite/");
  const isSuperAdminRoute = location.pathname.startsWith("/superadmin");
  const isAdminRoute = location.pathname.startsWith("/admin") || isSuperAdminRoute;

  useEffect(() => {
    window.scrollTo({
      left: 0,
      top: 0,
      // behavior: "smooth"
    });
  }, [location.pathname]);

  const navigate = useAnimatedNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const accessToken = getAccessToken();

    if (!accessToken) {
      clearAuthSession();
      dispatch(resetUserState());
      dispatch(setStatus({ isUser: false, isAdmin: false, isSuperAdmin: false }));

      if (!isAuthRoute) {
        navigate("/auth/login");
      }

      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const bootstrapUser = async () => {
      try {
        const { isUser, isAdmin, isSuperAdmin } = await UserAPI.fetchUserStatus();
        if (!isMounted) return; 

        dispatch(setStatus({ isUser, isAdmin, isSuperAdmin }));
        const hasAnyRole = isUser || isAdmin || isSuperAdmin;
        const hasPrivilegedRole = isAdmin || isSuperAdmin;

        if (!hasAnyRole) {
          clearAuthSession();
          dispatch(resetUserState());
          navigate("/auth/login");
          return;
        }

        // Keep role route in sync for single-role users, but do not override explicit auth pages.
        if (!isAuthRoute) {
          if (!isUser && isSuperAdmin && !isSuperAdminRoute) {
            navigate("/superadmin");
          } else if (!isUser && isAdmin && !isAdminRoute) {
            navigate("/admin");
          } else if (isUser && !hasPrivilegedRole && isAdminRoute) {
            navigate("/");
          }
        }

        const shouldLoadAdminInfo = hasPrivilegedRole && (isAdminRoute || !isUser);
        if (shouldLoadAdminInfo) {
          dispatch(fetchAdminInfo());
          return;
        }

        dispatch(fetchUserInfo());
      } catch {
        if (!isMounted) return;

        clearAuthSession();
        dispatch(resetUserState());
        dispatch(setStatus({ isUser: false, isAdmin: false, isSuperAdmin: false }));

        if (!isAuthRoute) {
          navigate("/auth/login");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    bootstrapUser();

    return () => {
      isMounted = false;
    };
  }, [dispatch, isAuthRoute, isAdminRoute, isSuperAdminRoute, navigate]);

  const loaded = useSelector(state => state.user.loaded);

  if ((loading || (getAccessToken() && !loaded)) && !isAuthRoute) {
    return <PageLoader />;
  }

  return <Outlet />;
}